import { formatToHours } from 'components/util/HoursDisplay';
import { formatAmount } from 'components/util/MoneyDisplay';
import type { WidgetFigure } from 'types/homeWidgetFigures';
import { getIntl, t } from 'utils/getTranslations';
import { useTimecardPrecision } from 'utils/useTimecardPrecision';

// Home is the new design's alone, so its figures are written in that design's locale, through `getIntl()`.

/** A share as a percentage. The change against the period before keeps a decimal; a target's share does not. */
export function formatPercent(ratio: number, fractionDigits = 0): string {
    return getIntl().formatNumber(ratio, { style: 'percent', maximumFractionDigits: fractionDigits });
}

/**
 * A change against the period before, to a decimal. Past 999.9% it is only said to be more: the chip that holds it
 * then has a widest, which the room kept for it counts on.
 */
export function formatChange(ratio: number): string {
    return Number((ratio * 100).toFixed(1)) > 999.9 ? `>${formatPercent(9.99)}` : formatPercent(ratio, 1);
}

/** A number short enough to stand over a bar, whatever its size: 86.4k for 86 400.5. Its unit is left to the total. */
export function formatCompact(value: number): string {
    return getIntl().formatNumber(value, { notation: 'compact', maximumFractionDigits: 1 });
}

export type FigureFormat = (value: number, short?: boolean) => string;

/**
 * Writes a value of the figure's unit: hours as every list does, an amount to the cent with the currency the figure
 * names, as `MoneyDisplay` does, a count as it is. Amounts arrive with every decimal the backend computed. `short`
 * writes the number as a bar's label and keeps its unit, for where the whole of it has no room.
 */
export function useFigureFormat({ unit, currencyCode }: Pick<WidgetFigure, 'unit' | 'currencyCode'>): FigureFormat {
    const { displayDecimalScale } = useTimecardPrecision();
    return (value, short) => {
        if (unit === 'HOURS') {
            return short
                ? `${formatCompact(value)}${t('time.hours-suffix')}`
                : formatToHours(value, displayDecimalScale);
        }
        if (unit === 'AMOUNT') {
            return short
                ? [formatCompact(value), currencyCode].filter(Boolean).join(' ')
                : formatAmount(value, currencyCode ?? '');
        }
        return short ? formatCompact(value) : getIntl().formatNumber(value, { maximumFractionDigits: 0 });
    };
}
